import { SECOES } from '@/conversas/filtros';

/**
 * Seções da inbox configuráveis por atendente: ordem, visibilidade e qual abre.
 *
 * Módulo PURO — nenhum React. A memória em disco segue o mesmo desenho da leva
 * de som (`notificacaoSom.js`): chave com o uid, `try/catch` em todo acesso, e
 * **nada além de preferência de exibição**. Não há dado de paciente aqui — só
 * ids de seção e uma ordem —, que é o que separa este módulo do
 * `lib/estadoDeSessao.js`, onde o `localStorage` é proibido por causa da LGPD.
 *
 * A recepcionista que passa o dia em "Aguardando" e nunca olha "Grupos" não tem
 * por que carregar Grupos na tela; quem cuida do arquivo quer "Encerradas" em
 * cima. As duas coisas na mesma lista, sem uma decidir pela outra.
 */

const IDS_CONHECIDOS = SECOES.map((s) => s.id);
const secaoPorId = (id) => SECOES.find((s) => s.id === id) ?? null;

/** A primeira seção declarada como aberta — o mesmo critério da lista. */
export const SECAO_INICIAL = SECOES.find((s) => s.abertaPorPadrao)?.id ?? IDS_CONHECIDOS[0];

/**
 * Padrão DERIVADO do `SECOES`, não copiado dele.
 *
 * Copiar a lista à mão criaria uma segunda fonte de verdade: acrescentar uma
 * seção em `filtros.js` e esquecer daqui faria a seção nova nascer invisível
 * para todo mundo, sem erro nenhum.
 */
export const preferenciasPadraoSecoes = () => ({
  ordem: [...IDS_CONHECIDOS],
  ocultas: [],
  padrao: SECAO_INICIAL,
});

export const chaveDasSecoes = (uid) => `odontosdr:secoes-inbox:${uid ?? 'anonimo'}`;

function deposito() {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/**
 * Põe em pé qualquer coisa vinda do disco.
 *
 * As três regras que importam, e por quê:
 *
 * 1. **id desconhecido é descartado** — sobra de uma seção removida do produto;
 *    mantê-lo faria a lista tentar renderizar uma seção que não existe mais.
 * 2. **seção nova é ANEXADA no fim, visível** — sem isso, uma seção criada
 *    depois nasceria invisível para todos os usuários antigos, e ninguém
 *    descobriria que ela existe (só quem limpasse o `localStorage`).
 * 3. **sempre sobra ao menos uma visível, e a `padrao` é uma delas** — a lista
 *    precisa ter o que abrir. Preferência inconsistente vira a primeira visível
 *    em vez de tela vazia.
 */
export function sanearSecoes(bruto) {
  const vistos = new Set();
  const ordem = [];
  for (const id of Array.isArray(bruto?.ordem) ? bruto.ordem : []) {
    if (IDS_CONHECIDOS.includes(id) && !vistos.has(id)) {
      vistos.add(id);
      ordem.push(id);
    }
  }
  // Regra 2: o que existe em SECOES e não veio do disco entra no fim.
  for (const id of IDS_CONHECIDOS) if (!vistos.has(id)) ordem.push(id);

  const brutasOcultas = Array.isArray(bruto?.ocultas) ? bruto.ocultas : [];
  let ocultas = ordem.filter((id) => brutasOcultas.includes(id));

  // Regra 3a: tudo oculto é estado impossível de desfazer pela própria tela —
  // não haveria painel de seção nenhuma para reexibir. Trata-se como disco
  // corrompido e volta ao "nada oculto".
  if (ocultas.length >= ordem.length) ocultas = [];

  const visiveis = ordem.filter((id) => !ocultas.includes(id));
  // Regra 3b: a padrão precisa ser uma seção que existe E está visível.
  const padrao = visiveis.includes(bruto?.padrao) ? bruto.padrao : visiveis[0];

  return { ordem, ocultas, padrao };
}

export function carregarSecoes(uid, armazem = deposito()) {
  try {
    const cru = armazem?.getItem(chaveDasSecoes(uid));
    if (!cru) return preferenciasPadraoSecoes();
    return sanearSecoes(JSON.parse(cru));
  } catch {
    return preferenciasPadraoSecoes();
  }
}

/** Grava. Devolve `false` quando o navegador não deixou. */
export function salvarSecoes(uid, prefs, armazem = deposito()) {
  try {
    armazem?.setItem(chaveDasSecoes(uid), JSON.stringify(sanearSecoes(prefs)));
    return Boolean(armazem);
  } catch {
    return false;
  }
}

/** Todas as seções na ordem escolhida, ocultas inclusive — é o que o painel edita. */
export const secoesOrdenadas = (prefs) =>
  sanearSecoes(prefs).ordem.map(secaoPorId).filter(Boolean);

/** O que a LISTA renderiza: na ordem, sem as ocultas. */
export function secoesVisiveisOrdenadas(prefs) {
  const p = sanearSecoes(prefs);
  return p.ordem.filter((id) => !p.ocultas.includes(id)).map(secaoPorId).filter(Boolean);
}

export const estaOculta = (prefs, id) => sanearSecoes(prefs).ocultas.includes(id);

/** A seção que o acordeão abre. Nunca devolve uma seção oculta. */
export const secaoPadrao = (prefs) => sanearSecoes(prefs).padrao;

/**
 * Por que o olho está desabilitado — ou `null` se dá para ocultar.
 *
 * Devolve o TEXTO, não um booleano: o painel precisa dizer o motivo no tooltip,
 * e um booleano obrigaria a tela a redescobrir qual das duas regras barrou.
 */
export function motivoParaNaoOcultar(prefs, id) {
  const p = sanearSecoes(prefs);
  if (p.ocultas.includes(id)) return null; // já oculta: o olho serve para MOSTRAR
  if (p.padrao === id) return 'Não é possível ocultar a seção padrão';
  /**
   * A guarda de baixo quase nunca é a que fala: como o saneamento mantém a
   * padrão sempre visível, a ÚLTIMA visível é necessariamente a padrão, e a
   * linha acima já barrou. Ela fica como rede para o caso de o saneamento
   * mudar — a invariante "≥1 visível" não pode depender de um efeito colateral
   * de outra regra.
   */
  const visiveis = p.ordem.filter((x) => !p.ocultas.includes(x));
  if (visiveis.length <= 1) return 'A lista precisa de ao menos uma seção visível';
  return null;
}

export const podeOcultar = (prefs, id) => motivoParaNaoOcultar(prefs, id) === null;

/** Oculta ou mostra. Barrado pelas guardas, devolve as preferências INTACTAS. */
export function alternarOculta(prefs, id) {
  const p = sanearSecoes(prefs);
  if (!p.ordem.includes(id)) return p;
  if (p.ocultas.includes(id)) {
    return sanearSecoes({ ...p, ocultas: p.ocultas.filter((x) => x !== id) });
  }
  if (!podeOcultar(p, id)) return p;
  return sanearSecoes({ ...p, ocultas: [...p.ocultas, id] });
}

/**
 * Sobe (-1) ou desce (+1) uma posição.
 *
 * A troca é com o vizinho imediato em `ordem`, oculta ou não — porque o painel
 * lista TODAS as seções, inclusive as ocultas (é onde se reexibe uma). Pular a
 * oculta faria o item saltar duas linhas na tela sem motivo visível.
 */
export function moverSecao(prefs, id, direcao) {
  const p = sanearSecoes(prefs);
  const i = p.ordem.indexOf(id);
  const j = i + direcao;
  if (i < 0 || j < 0 || j >= p.ordem.length) return p;
  const ordem = [...p.ordem];
  [ordem[i], ordem[j]] = [ordem[j], ordem[i]];
  return sanearSecoes({ ...p, ordem });
}

/** Define a seção que abre. Seção oculta não pode ser padrão. */
export function definirPadrao(prefs, id) {
  const p = sanearSecoes(prefs);
  if (!p.ordem.includes(id) || p.ocultas.includes(id)) return p;
  return sanearSecoes({ ...p, padrao: id });
}
